import { autoUpdate, flip, FloatingFocusManager, offset, shift, useClick, useDismiss, useFloating, useInteractions, useRole } from '@floating-ui/react';
import { Calendar as CalendarIcon } from '@wix/wix-ui-icons-common/on-stage';
import { IconButton, TextButton, TextButtonPriority, TextField, TextFieldTheme } from '@wix/wix-ui-tpa/cssVars';
import classNames from 'classnames';
import React, { lazy, Suspense, useCallback, useRef, useState } from 'react';
import { useConfig } from '../../../contexts/config-context';
import { useFocusFieldEvent } from '../../../contexts/events-context';
import { ForcedState, useForcedState } from '../../../contexts/forced-state-context';
import { useTranslation } from '../../../contexts/translation-context';
import { classes } from './date-picker-input-wut.st.css';
import { getLocalizedValue } from './date-picker-utils';
const Calendar = /*#__PURE__*/ lazy(()=>import('../calendar/calendar-wut'));
export const DatePickerInputWUT = ({ inputId, ariaDescribedBy, label, placeholder, required, value, onChange, onFocus, onBlur, hasError, validation, firstDayOfWeek, availability, disabled, fieldId })=>{
    const { t } = useTranslation();
    const { regionalFormat, isMobile, isComposer, isPreview, isEditor } = useConfig();
    const forcedHover = useForcedState().includes(ForcedState.INPUT_FIELDS_HOVER);
    const inputRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [autoFocus, setAutoFocus] = useState(false);
    const focusInput = useCallback(()=>{
        inputRef.current?.focus();
    }, []);
    const calendarIconRef = useRef(null);
    // TODO: move this to a reusable component
    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange: (_, _event, reason)=>{
            if (reason === 'outside-press' || reason === 'escape-key') {
                closeCalendar();
            } else {
                openCalendar();
            }
        },
        middleware: [
            offset(8),
            flip(),
            shift({
                crossAxis: isMobile && true
            })
        ],
        whileElementsMounted: autoUpdate,
        placement: 'bottom-start'
    });
    const dismiss = useDismiss(context);
    const role = useRole(context);
    const click = useClick(context);
    const { getReferenceProps, getFloatingProps } = useInteractions([
        dismiss,
        role,
        click
    ]);
    const focusCalendarIcon = ()=>{
        calendarIconRef.current?.focus();
    };
    const openCalendar = ()=>{
        if (!isOpen && !disabled) {
            setIsOpen(true);
        }
    };
    const closeCalendar = ()=>{
        setAutoFocus(false);
        setIsOpen(false);
        focusInput();
        scrollInputIntoView();
    };
    const onInputClearButtonClicked = ()=>{
        onChange(null);
        focusCalendarIcon();
    };
    const handleBlur = (ev)=>{
        if (!ev.relatedTarget?.closest('[data-hook="date-picker-calendar"]')) {
            onBlur();
        }
    };
    const scrollInputIntoView = ()=>{
        inputRef.current?.scrollIntoView?.({
            block: 'center',
            inline: 'nearest'
        });
    };
    useFocusFieldEvent(()=>{
        inputRef.current?.focus({
            preventScroll: true
        });
    }, fieldId);
    const handleKeyDown = (event)=>{
        switch(event.key){
            case 'Tab':
            case 'Escape':
                {
                    if (isOpen) {
                        closeCalendar();
                    }
                    onBlur();
                    return;
                }
            case ' ':
            case 'Enter':
                {
                    if (!isOpen && !disabled) {
                        openCalendar();
                    }
                    return;
                }
            case 'Backspace':
                {
                    onChange(null);
                    if (isOpen) {
                        closeCalendar();
                    }
                    return;
                }
            case 'ArrowUp':
            case 'ArrowDown':
            case 'ArrowLeft':
            case 'ArrowRight':
                {
                    if (isOpen) {
                        event.preventDefault();
                        setAutoFocus(true);
                    }
                    return;
                }
            default:
                return;
        }
    };
    const ariaLabel = value ? `${value} ${label}` : label;
    const mobileMode = isMobile && !isComposer && !isPreview && !isEditor;
    const handleRef = (ref)=>{
        inputRef.current = ref;
    };
    return /*#__PURE__*/ React.createElement("div", {
        className: classes.wrapper
    }, /*#__PURE__*/ React.createElement("div", {
        ref: refs.setReference,
        "data-hook": "popover-element",
        ...getReferenceProps()
    }, /*#__PURE__*/ React.createElement(TextField, {
        "data-hook": "date-picker-input",
        id: inputId,
        inputRef: handleRef,
        value: value ? getLocalizedValue(value, regionalFormat) : '',
        placeholder: placeholder,
        className: classNames(classes.datePickerInput, classes.theme, {
            [classes.error]: hasError,
            [classes.hover]: forcedHover,
            [classes.calendarIconDisabled]: disabled
        }),
        prefix: /*#__PURE__*/ React.createElement(IconButton, {
            icon: /*#__PURE__*/ React.createElement(CalendarIcon, null),
            onClick: openCalendar,
            "data-hook": "date-picker-calendar-icon",
            "aria-roledescription": t('date-picker.a11y.calendar-button.role-description'),
            "aria-label": ariaLabel,
            "aria-describedby": ariaDescribedBy,
            "aria-invalid": hasError,
            "aria-selected": Boolean(value),
            innerRef: calendarIconRef,
            onKeyDown: handleKeyDown
        }),
        disabled: disabled,
        error: hasError,
        newErrorMessage: true,
        onClick: openCalendar,
        onKeyDown: handleKeyDown,
        onClear: onInputClearButtonClicked,
        withClearButton: true,
        clearButtonAriaLabel: `${value} ${label} ${t('date-picker.a11y.clear-button')}`,
        inputMode: "none",
        readOnly: true,
        theme: TextFieldTheme.Box,
        required: required,
        onBlur: handleBlur,
        onFocus: onFocus,
        tabIndex: -1,
        "aria-hidden": true
    })), isOpen && /*#__PURE__*/ React.createElement(FloatingFocusManager, {
        context: context,
        disabled: true
    }, /*#__PURE__*/ React.createElement("div", {
        ref: refs.setFloating,
        style: {
            ...floatingStyles,
            zIndex: 1000
        },
        className: classNames(classes.content, {
            [classes.mobilePopoverContent]: mobileMode
        }),
        ...getFloatingProps()
    }, /*#__PURE__*/ React.createElement(Suspense, {
        fallback: /*#__PURE__*/ React.createElement("div", {
            className: classes.calendarSkeleton
        })
    }, /*#__PURE__*/ React.createElement(Calendar, {
        value: value,
        validation: validation,
        firstDayOfWeek: firstDayOfWeek,
        availability: availability,
        onChange: onChange,
        closeCalendar: closeCalendar,
        focusInput: focusInput,
        autoFocus: autoFocus
    }), mobileMode && /*#__PURE__*/ React.createElement("div", {
        className: classes.closeIconContainer
    }, /*#__PURE__*/ React.createElement(TextButton, {
        onClick: closeCalendar,
        priority: TextButtonPriority.primary
    }, t('date-picker.calendar.close-button')))))));
};

//# sourceMappingURL=date-picker-input-wut.js.map